import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import md5 from 'md5';
import icp from '../../assets/icp-brasil.png';
import header from '../../assets/icm-header.jpg';
import {
  getBase64PDFImage,
  getBase64PDFImagePNG,
  prefixo,
} from '../../util/utils';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const ENDERECO_ICM =
  'Av. Vicente Ferreira, 780 - Marília/SP - CEP: 17515-000';
const TELEFONE_ICM = '(14) 3402-5252';

const campoLinha = label => ({
  text: label,
  margin: [0, 2, 0, 8],
  fontSize: 9,
});

const quadroEmitenteTitulo = (emitente, dataHoraEmissao, viaLabel) => ({
  margin: [0, 8, 0, 0],
  table: {
    widths: ['*', '*'],
    body: [
      [
        {
          stack: [
            {
              text: 'IDENTIFICAÇÃO DO EMITENTE',
              bold: true,
              fontSize: 9,
              margin: [0, 0, 0, 4],
            },
            { text: emitente?.nome || '', bold: true, fontSize: 10 },
            {
              text: `CRM: ${emitente?.crm || ''}${emitente?.uf ? ` — ${emitente.uf}` : ''}`,
              fontSize: 9,
            },
            { text: ENDERECO_ICM, fontSize: 9 },
            { text: `Tel.: ${TELEFONE_ICM}`, fontSize: 9 },
          ],
          margin: [4, 4, 4, 4],
        },
        {
          stack: [
            {
              text: 'RECEITUÁRIO CONTROLE ESPECIAL',
              bold: true,
              alignment: 'right',
              fontSize: 11,
            },
            {
              text: dataHoraEmissao,
              alignment: 'right',
              fontSize: 9,
              margin: [0, 4, 0, 4],
            },
            {
              text: viaLabel,
              alignment: 'right',
              bold: true,
              fontSize: 10,
              margin: [0, 0, 0, 8],
            },
          ],
          margin: [4, 4, 4, 4],
        },
      ],
    ],
  },
  layout: {
    hLineWidth: () => 1,
    vLineWidth: () => 1,
  },
});

const quadroCompradorFornecedor = () => ({
  margin: [0, 2, 0, 0],
  table: {
    widths: ['*', '*'],
    body: [
      [
        {
          stack: [
            { text: 'IDENTIFICAÇÃO DO COMPRADOR', bold: true, fontSize: 9 },
            campoLinha('Nome: _________________________________________'),
            campoLinha('RG: ___________________________________________'),
            campoLinha('Endereço: _____________________________________'),
            campoLinha('Telefone: ______________________________________'),
            campoLinha('Cidade/UF: ____________________________________'),
          ],
          margin: [4, 4, 4, 4],
        },
        {
          stack: [
            { text: 'IDENTIFICAÇÃO DO FORNECEDOR', bold: true, fontSize: 9 },
            campoLinha('Data da dispensação: ____/____/________'),
            campoLinha(''),
            campoLinha(''),
            campoLinha('Assinatura do Farmacêutico:'),
            campoLinha('__________________________________________________'),
          ],
          margin: [4, 4, 4, 4],
        },
      ],
    ],
  },
  layout: {
    hLineWidth: () => 1,
    vLineWidth: () => 1,
  },
});

export function buildReceitaControladaViaContent({
  emitente,
  receita,
  viaLabel,
  dataHoraEmissao,
}) {
  const textoPrescricao = receita.texto || receita.descricao || ' ';

  return [
    {
      ...quadroEmitenteTitulo(emitente, dataHoraEmissao, viaLabel),
      margin: [0, 0, 0, 12],
    },
    {
      text: textoPrescricao,
      fontSize: 10,
      margin: [20, 0, 0, 8],
    },
  ];
}

function buildBlocoAssinaturaDigital({
  receita,
  qrcodeBase64,
  icpLogoBase64,
  assinaturaDigital,
  isDoctorLoggedIn,
  currentPage,
  pageCount,
}) {
  return {
    table: {
      widths: [50, '*', '*', 80],
      body: [
        [
          assinaturaDigital
            ? {
              image: icpLogoBase64,
              width: 70,
              border: [false, false, false, false],
            }
            : { text: '', border: [false, false, false, false] },
          {
            colSpan: 2,
            stack: [
              ...(isDoctorLoggedIn
                ? [
                  {
                    text: assinaturaDigital
                      ? 'Documento assinado digitalmente'
                      : 'Documento assinado eletronicamente',
                    alignment: 'center',
                    bold: true,
                    fontSize: 8,
                  },
                  {
                    text: `Autenticação: ${md5(JSON.stringify(receita || ''))}`,
                    alignment: 'center',
                    fontSize: 7,
                  },
                ]
                : []),
              {
                text: `Médico: ${prefixo(receita)} ${receita.nomeMedico || ''}`,
                alignment: 'center',
                fontSize: 10,
              },
              {
                text: `CPF: ${receita.cpf || ''} - CRM: ${receita.crm || ''}`,
                alignment: 'center',
                fontSize: 10,
              },
              {
                text: `Página ${currentPage} de ${pageCount}`,
                alignment: 'center',
                fontSize: 7,
              },
            ],
            border: [false, false, false, false],
          },
          {},
          ...(isDoctorLoggedIn && qrcodeBase64
            ? [
              {
                image: qrcodeBase64,
                width: 65,
                alignment: 'right',
                border: [false, false, false, false],
              },
            ]
            : [{ text: '', border: [false, false, false, false] }]),
        ],
      ],
    },
    layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
  };
}

function buildFooterReceitaControlada({
  receita,
  qrcodeBase64,
  icpLogoBase64,
  assinaturaDigital,
  assinaturaEletronica,
  isDoctorLoggedIn,
  papelTimbrado,
}) {
  const omitirAssinatura = isDoctorLoggedIn && assinaturaEletronica;

  return function footer(currentPage, pageCount) {
    const stack = [];

    if (!omitirAssinatura) {
      stack.push(
        buildBlocoAssinaturaDigital({
          receita,
          qrcodeBase64,
          icpLogoBase64,
          assinaturaDigital,
          isDoctorLoggedIn,
          currentPage,
          pageCount,
        })
      );
    }

    stack.push(quadroCompradorFornecedor());

    return {
      margin: papelTimbrado ? [140, 0, 40, 0] : [40, 0, 40, 0],
      stack,
    };
  };
}

export function buildReceitaControladaDocDefinition({
  headerImage,
  emitente,
  receita,
  dataHoraEmissao,
  qrcodeBase64,
  assinaturaDigital,
  assinaturaEletronica,
  isDoctorLoggedIn,
  icpLogoBase64 = '',
}) {
  const viaParams = {
    emitente,
    receita,
    dataHoraEmissao,
  };

  const content = [
    ...buildReceitaControladaViaContent({
      ...viaParams,
      viaLabel: '1ª via Farmácia',
    }),
    { text: '', pageBreak: 'after' },
    ...buildReceitaControladaViaContent({
      ...viaParams,
      viaLabel: '2ª via Paciente',
    }),
  ];

  const papelTimbrado = !headerImage;
  const marginBottom = assinaturaEletronica ? 150 : 220;

  return {
    pageSize: 'A4',
    pageMargins: papelTimbrado ? [140, 100, 20, marginBottom] : [40, 110, 20, marginBottom],
    header: headerImage
      ? {
        image: headerImage,
        width: 450,
        alignment: 'center',
        margin: [0, 5, 0, 0],
      }
      : undefined,
    content,
    footer: buildFooterReceitaControlada({
      receita,
      qrcodeBase64,
      icpLogoBase64,
      assinaturaDigital,
      assinaturaEletronica,
      isDoctorLoggedIn,
      papelTimbrado,
    }),
  };
}

export default async function generateReceitaControladaPDF({
  receita,
  tipo = 'imprimirNormal',
  qrcodeBase64,
  assinaturaDigital,
  assinaturaEletronica,
  isDoctorLoggedIn,
  emitente = null,
}) {
  const headerImage =
    tipo === 'imprimirTimbrado' || tipo === 'envioWpp'
      ? await getBase64PDFImage(header)
      : null;
  const icpLogoBase64 = await getBase64PDFImagePNG(icp);
  const dataHoraEmissao = new Date()
    .toLocaleString('pt-BR', { hour12: false })
    .replace(',', '');

  const docDefinition = buildReceitaControladaDocDefinition({
    emitente,
    receita,
    dataHoraEmissao,
    qrcodeBase64,
    assinaturaDigital,
    assinaturaEletronica,
    isDoctorLoggedIn,
    icpLogoBase64,
    headerImage,
  });

  return pdfMake.createPdf(docDefinition);
}
